import { Alert, Checkbox, Divider, Form, Input, List, Spin, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { Banner } from 'components/ui/Banner';
import Card from 'components/Card/Card';
import { familyService } from 'services';
import { getFirstName, getFullName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import dayjs from 'dayjs';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import { getSpouseTitle, getChildRelationshipToAddlAdult } from '../household/constants';
import Select from 'components/Select/Select';

const RELATIONSHIP_TO_SPONSOR_MAP = {
  Child: 'Child',
  Stepchild: 'Step Child',
  FosterChild: 'Foster Child',
  LegalGuardian: 'Legal Guardian/Ward',
};

export function FamilyApplicationChildrenEligibilityCertification({
  application,
  setApplication,
  loading,
  setStep,
  readOnly = false,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const stepConfig = useAtomValue(stepConfigAtom);

  const personCompletingFormValue = Form.useWatch('personCompletingForm', form);

  // Memoize sponsor info
  const { sponsorFullName, sponsorGrade } = useMemo(() => {
    const sponsor = application?.applicant;
    const fullName = `${sponsor?.firstName || ''} ${sponsor?.lastName || ''}`.trim();
    const grade = sponsor?.militaryDetails?.grade?.name || sponsor?.militaryDetails?.grade?.title || '';
    return { sponsorFullName: fullName, sponsorGrade: grade };
  }, [application?.applicant]);

  // Memoize additional adult info
  const { additionalAdult, additionalAdultFullName } = useMemo(() => {
    const adult = application?.additionalParents?.[0];
    return {
      additionalAdult: adult,
      additionalAdultFullName: getFullName(adult),
    };
  }, [application?.additionalParents]);

  // Memoize dropdown options for person completing form
  const personCompletingFormOptions = useMemo(
    () => [
      {
        id: 'sponsor',
        title: sponsorFullName || 'Service Member',
      },
      ...(additionalAdult
        ? [
            {
              id: 'additionalAdult',
              title: additionalAdultFullName || 'Other Adult',
            },
          ]
        : []),
    ],
    [sponsorFullName, additionalAdult, additionalAdultFullName],
  );

  // Determine if sponsor is completing based on personCompletingFormValue
  const isSponsorCompleting = personCompletingFormValue?.id === 'sponsor';

  // Get relationship to sponsor based on selected person
  const relationshipToSponsor = useMemo(() => {
    if (!personCompletingFormValue) return '';
    if (isSponsorCompleting) return 'Self';

    const spouseTitle = getSpouseTitle(application);
    return spouseTitle.charAt(0).toUpperCase() + spouseTitle.slice(1);
  }, [application, isSponsorCompleting, personCompletingFormValue]);

  // Memoize children list data
  const childrenListData = useMemo(
    () =>
      (application?.children || []).map((child, index) => ({
        key: index,
        name: getFullName(child),
        dateOfBirth: child.dateOfBirth ? dayjs(child.dateOfBirth).format(DATE_FORMAT_SENTENCE) : '',
        relationship: RELATIONSHIP_TO_SPONSOR_MAP[child.relationToApplicant] || child.relationToApplicant || '',
      })),
    [application?.children],
  );

  // Memoize child names for certification statement
  const { childFirstNames, childNamesString } = useMemo(() => {
    const firstNames = (application?.children || []).map((child) => getFirstName(child)).filter(Boolean);
    const namesString =
      firstNames.length > 1
        ? `${firstNames.slice(0, -1).join(', ')}, and ${firstNames[firstNames.length - 1]}`
        : firstNames[0] || '';
    return { childFirstNames: firstNames, childNamesString: namesString };
  }, [application?.children]);

  // Memoize selected person's full name
  const selectedPersonFullName = useMemo(
    () => (isSponsorCompleting ? sponsorFullName : additionalAdultFullName),
    [isSponsorCompleting, sponsorFullName, additionalAdultFullName],
  );

  // Memoize relationship statements for children
  const childRelationshipStatements = useMemo(() => {
    if (!personCompletingFormValue) return [];

    return (application?.children || []).map((child) => {
      const relationshipLabel = isSponsorCompleting
        ? getChildRelationshipToAddlAdult(child.relationToApplicant)
        : getChildRelationshipToAddlAdult(child.relationToAdditionalAdult);

      return {
        childFullName: getFullName(child),
        relationshipLabel: relationshipLabel.toLowerCase(),
      };
    });
  }, [application?.children, isSponsorCompleting, personCompletingFormValue]);

  useEffect(() => {
    const personCompletingForm =
      application?.selfcertAcceptedById === application?.applicant?.id
        ? personCompletingFormOptions.find((opt) => opt.id === 'sponsor')
        : application?.selfcertAcceptedById === additionalAdult?.id
        ? personCompletingFormOptions.find((opt) => opt.id === 'additionalAdult')
        : undefined;

    form.setFieldsValue({
      personCompletingForm,
      selfcertAccepted: application?.selfcertAccepted || false,
      selfcertAcceptedBy: application?.selfcertAcceptedBy || '',
    });
  }, [application, form, personCompletingFormOptions, additionalAdult]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION,
          _newApplication,
          stepConfig,
        );
        setStep({
          applicationId: _newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const payload = {
          selfcertAcceptedBy: values.selfcertAcceptedBy,
          selfcertAcceptedById:
            values.personCompletingForm?.id === 'sponsor' ? application.applicant?.id : additionalAdult?.id,
        };

        await familyService.acceptSelfCert(application.id, payload);
        const updatedApplication = await familyService.getApplication(application.id);
        setApplication(updatedApplication);
        _goToNextStep(updatedApplication);
      } catch (error) {
        console.error('Error saving eligibility certification:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, readOnly, setApplication, setStep, stepConfig, additionalAdult],
  );

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Child Eligibility Certification" />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner question="Confirm your children's eligibility" className="my-12" />

          <Typography.Paragraph>
            You've added your children to this application. The certification below confirms they are eligible for
            military child and youth programs. Please review the information and sign to continue.
          </Typography.Paragraph>

          {/* Form Card */}
          <Card title="Child Self-Certification Statement" className="mt-8 mb-8" color="primary">
            <Typography.Paragraph className="mb-6" type="secondary">
              This form is used to determine child/youth eligibility for the Child Care Fee Assistance and Respite
              programs administered by Child Care Aware® of America in the absence of a birth certificate, military
              identification card or orders identifying the eligible child(ren)/youth.
            </Typography.Paragraph>

            {/* Read-only: Name of Military Sponsor */}
            <Form.Item label="Name of Military Sponsor" className="mb-8">
              <Input disabled value={sponsorFullName || 'Not provided'} className="w-full max-w-md" />
            </Form.Item>

            {/* Read-only: Rank of Military Sponsor */}
            <Form.Item label="Rank of Military Sponsor" className="mb-8">
              <Input disabled value={sponsorGrade || 'Not provided'} className="w-full max-w-md" />
            </Form.Item>

            {/* Dropdown: Person Completing This Form */}
            <Form.Item
              name="personCompletingForm"
              label="Person completing this form"
              rules={[{ required: true, message: 'Please select who is completing this form' }]}
              className="mb-8"
            >
              <Select options={personCompletingFormOptions} className="w-full max-w-md" placeholder="Select" />
            </Form.Item>

            {/* Read-only: Relationship to Sponsor */}
            {personCompletingFormValue && (
              <Form.Item
                label="Relationship to Sponsor (eg. Spouse, designated legal guardian, other)"
                className="mb-6"
              >
                <Input disabled value={relationshipToSponsor} className="w-full max-w-md" />
              </Form.Item>
            )}

            {/* Disclaimer text */}
            <Alert
              message="Note:"
              description={
                <>
                  <p>
                    By virtue of their relationship to this Military Sponsor (biological, adoption, step child(ren),
                    legal guardian, or other relationship which authorizes eligibility) the children listed below are
                    eligible to participate in military sponsored child and youth programs.
                  </p>

                  <p className="mt-2">
                    Misrepresentation or falsifying this information may subject the individual to prosecution under the
                    Uniform Code of Military Justice (UCMJ) and/or applicable State and Federal Laws.
                  </p>
                </>
              }
              showIcon
              className="mb-8"
            />

            {/* Children List */}
            <div className="mb-8">
              <List
                bordered
                dataSource={childrenListData}
                className="bg-white border border-solid border-[#bcd0ed]"
                renderItem={(item) => (
                  <List.Item className="!px-6 !py-4">
                    <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Typography.Text className="block text-gray-500 mb-1">Child Name</Typography.Text>
                        <Typography.Text strong className="text-gray-900">
                          {item.name}
                        </Typography.Text>
                      </div>
                      <div>
                        <Typography.Text className="block text-gray-500 mb-1">Date of Birth</Typography.Text>
                        <Typography.Text strong className="text-gray-900">
                          {item.dateOfBirth}
                        </Typography.Text>
                      </div>
                      <div>
                        <Typography.Text className="block text-gray-500 mb-1">Relationship to Sponsor</Typography.Text>
                        <Typography.Text strong className="text-gray-900">
                          {item.relationship}
                        </Typography.Text>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>

            <Divider />

            <Typography.Paragraph className="mb-6 font-medium">
              I certify that {childNamesString} {childFirstNames.length > 1 ? 'are' : 'is'} eligible to participate in
              military child and youth programs.
            </Typography.Paragraph>

            {/* Checkbox */}
            <Form.Item
              name="selfcertAccepted"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error('You must check this box to continue')),
                },
              ]}
              className="mb-8"
            >
              <Checkbox className="flex items-start">
                <Typography.Text type="secondary">
                  By checking this box, I electronically sign this Child Self-Certification form and attest that the
                  above information is true and accurate. I understand this digital signature has the same legal effect
                  and enforce-ability as a handwritten signature.
                </Typography.Text>
              </Checkbox>
            </Form.Item>

            {/* Full Name Input */}
            <Form.Item
              name="selfcertAcceptedBy"
              label="Enter your full name to certify the information above"
              rules={[{ required: true, message: 'Please enter your full name' }]}
              className="mb-8"
            >
              <Input className="w-full max-w-md" placeholder="Full name" data-testid="selfcert-full-name-input" />
            </Form.Item>

            {/* Relationship statements */}
            {personCompletingFormValue && (
              <Card title="Relationship to children listed above">
                <ol className="list-decimal ml-4 space-y-4">
                  {childRelationshipStatements.map((statement, index) => (
                    <li key={index}>
                      <Typography.Text className="block">
                        <span className="font-semibold">{statement.childFullName}</span> is the{' '}
                        <span className="font-semibold">{statement.relationshipLabel}</span> of{' '}
                        <span className="font-semibold">{selectedPersonFullName}</span>
                      </Typography.Text>
                    </li>
                  ))}
                </ol>
              </Card>
            )}
          </Card>
        </div>

        <ApplicationWizardActions
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_ELIGIBILITY_CERTIFICATION,
                application,
                stepConfig,
              );
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            'data-testid': 'eligibility-certification-back-button',
          }}
          nextButtonProps={{
            text: 'Continue',
            loading: submitting,
            'data-testid': 'eligibility-certification-next-button',
          }}
        />
      </Form>
    </>
  );
}
